import axios from 'axios'
import { get, isEmpty, omit } from 'lodash'
import { responseHandler, apiErrorHandler } from '../actions/handlers.action'
import {
  getSlots,
  getBoardingPatternDataAction,
  getGuestCount
} from '../actions/slots.action'
import { showNotification } from '../actions/notification.action'
import { getShipTime } from '../helpers/util'
import {
  GET_SLOT_QUERY,
  GET_VOYAGE_BY_ID_QUERY,
  UPDATE_BOARDING_NUMBER_MUTATION,
  UPDATE_SLOTS_MUTATION
} from '../graphql/queries/slotQueries'
import { GUEST_COUNT_QUERY } from '../graphql/queries/guestQueries'
import { queryResolver } from '../helpers/gqlUtil'
import { slotsMapper, voyageItinerariesMapper } from '../helpers/mapper'
import {
  convertDateTo_YYYY_MM_DD_T_HH_mm_ss,
  defaultDateFormat
} from '../helpers/dateUtil'

export const getSlotsData = () => (dispatch, getState) => {
  const state = getState()
  const voyage = get(state, 'voyage', {})
  const { embarkDate, shipCode, voyageNumber } = voyage
  return queryResolver({
    query: GET_SLOT_QUERY,
    variables: {
      filter: {
        embarkDate,
        shipCode,
        voyageNumber
      }
    }
  })
    .then(result => {
      const response = {
        data: slotsMapper(get(result, 'data.searchBoardingNumber', []))
      }
      response.shipTime = getShipTime(get(state.masterdata, 'shipTimes', {}))
      responseHandler(dispatch, response, getSlots)
      return response
    })
    .catch(function(err) {
      apiErrorHandler(dispatch, err)
    })
}

export const updateSlotsData = (
  body,
  isMovingSlots = false,
  offset,
  isBoardingSlotReport = false,
  voyageContext
) => (dispatch, getState) => {
  const state = getState()
  const voyage =
    isBoardingSlotReport && voyageContext
      ? voyageContext
      : get(state, 'voyage', {})
  const { embarkDate, shipCode, voyageNumber } = voyage
  const boardingNumberData = (body || []).map(
    ({ boardingNumberId, activeSlotTime, expectedGuestCount, ...rest }) => {
      const mappedBoarding = {
        ...rest,
        id: boardingNumberId,
        expectedGuestCount: parseInt(expectedGuestCount),
        activeSlotTime: !isEmpty(activeSlotTime) ? activeSlotTime : null
      }
      return omit(mappedBoarding, [
        'oldBoardingSlotStartTime',
        'oldBoardingSlotEndTime'
      ])
    }
  )

  const input = {
    embarkDate: defaultDateFormat(embarkDate),
    shipCode,
    voyageNumber,
    boardingNumberData
  }

  if (!isBoardingSlotReport) {
    input.isMovingSlots = isMovingSlots
    input.offset = offset
  }

  return queryResolver({
    mutation: UPDATE_BOARDING_NUMBER_MUTATION,
    variables: {
      input
    }
  })
    .then(response => {
      dispatch(
        showNotification({
          class: 'is-success',
          message: 'Boarding updated successfully.'
        })
      )
      return response
    })
    .catch(function(err) {
      dispatch(
        showNotification({
          class: 'is-danger',
          message: 'Oops! Some technical issue occured. Please try again.'
        })
      )
      apiErrorHandler(dispatch, err)
    })
}

export const postSlotsData = body => (dispatch, getState) => {
  const state = getState()
  const voyage = get(state, 'voyage', {})
  body.forEach(item => {
    item.embarkDate = voyage.embarkDate
    item.boardingNumber = item.boardingNumber.toString()
    item.slotNumber = item.slotNumber.toString()
  })
  return axios
    .post(
      `${get(state, 'startup.slotsUrl')}?embarkdate=${
        voyage.embarkDate
      }&shipcode=${voyage.shipCode}&voyagenumber=${voyage.voyageNumber}`,
      body
    )
    .then(response => {
      if (response.status === 200) {
        dispatch(
          showNotification({
            class: 'is-success',
            message: 'Boarding updated successfully.'
          })
        )
      } else {
        dispatch(
          showNotification({
            class: 'is-danger',
            message: 'Oops! Some technical issue occured. Please try again.'
          })
        )
      }
      return response.data
    })
    .catch(function(err) {
      dispatch(
        showNotification({
          class: 'is-danger',
          message: 'Oops! Some technical issue occured. Please try again.'
        })
      )
      apiErrorHandler(dispatch, err)
    })
}

export const getBoardingPatternData = () => (dispatch, getState) => {
  const state = getState()
  const voyageId = get(state, 'voyage.voyageId')
  return queryResolver({
    query: GET_VOYAGE_BY_ID_QUERY,
    variables: {
      voyageId
    }
  })
    .then(response => {
      const voyage = get(response, 'data.voyage', {})
      const { id, name, isDeleted, itineraries, ...rest } = omit(
        voyage,
        '__typename'
      )
      const updatedObj = {
        nameKey: name,
        isDeleted: isDeleted || false,
        voyageId: id,
        ...rest,
        voyageItineraries: voyageItinerariesMapper(itineraries)
      }
      responseHandler(dispatch, updatedObj, getBoardingPatternDataAction)
      return response
    })
    .catch(function(err) {
      apiErrorHandler(dispatch, err)
    })
}

export const getGuestStatusCount = () => (dispatch, getState) => {
  const state = getState()
  const voyage = get(state, 'voyage', {})
  const embarkDateRange = {
    embarkDate: convertDateTo_YYYY_MM_DD_T_HH_mm_ss(get(voyage, 'embarkDate')),
    debarkDate: convertDateTo_YYYY_MM_DD_T_HH_mm_ss(get(voyage, 'debarkDate'))
    // boardingInventorySummary: true,
    // shipCode: shipCode
  }

  return queryResolver({
    query: GUEST_COUNT_QUERY,
    variables: {
      embarkDateRange
    }
  })
    .then(response => {
      const mappedResponse = get(
        response,
        'data.guestBoardingStatsByDateRange',
        {}
      )
      responseHandler(dispatch, mappedResponse, getGuestCount)
      return mappedResponse
    })
    .catch(function(err) {
      apiErrorHandler(dispatch, err)
    })
}

export const postBoardingPatternData = body => (dispatch, getState) => {
  const state = getState()
  const url = `${get(state, 'startup.voyagesURL')}`
  return axios
    .post(url, body)
    .then(response => {
      dispatch(
        showNotification({
          class: 'is-success',
          message: 'Boarding Pattern updated successfully.'
        })
      )
      return response
    })
    .catch(function(err) {
      dispatch(
        showNotification({
          class: 'is-danger',
          message: 'Oops! Some technical issue occured. Please try again.'
        })
      )
      apiErrorHandler(dispatch, err)
    })
}

export const postGuestSlots = body => (dispatch, getState) => {
  const state = getState()
  const voyage = get(state, 'voyage', {})
  body.forEach(item => {
    item.embarkDate = voyage.embarkDate
  })
  return queryResolver({
    mutation: UPDATE_SLOTS_MUTATION,
    variables: {
      input: {
        embarkDate: voyage.embarkDate,
        shipCode: voyage.shipCode,
        voyageNumber: voyage.voyageNumber,
        guestBoardingNumbers: body
      }
    }
  })
    .then(response => {
      if (get(response, 'data.updateGuestBoardingNumbers', false)) {
          dispatch(
          showNotification({
            class: 'is-success',
            message: 'Boarding updated successfully.'
          })
        )
      }
      return response.data
    })
    .catch(function(err) {
      dispatch(
        showNotification({
          class: 'is-danger',
          message: 'Oops! Some technical issue occured. Please try again.'
        })
      )
      apiErrorHandler(dispatch, err)
    })
}
